/** `/notification_timeline/` — the guest-lifecycle overview.
 *
 * Hand-authored wrapper (console convention: feature modules never depend on
 * generated types). Three calls, deliberately separate:
 *
 * - `getTimeline`  the effective set for one tenant, positioned
 * - `getTimelineStats`  30-day fire counts, fetched as its own query so the
 *   timeline paints before the numbers arrive
 * - `toggleEvent` / `toggleDeliveryMethod`  the quick actions
 */
import { apiGet, apiPatch, apiPost } from './client';

export type ApiBand = 'lifecycle' | 'reactive' | 'itinerary' | 'unknown';

export interface ApiEventPosition {
  phase: string;
  band: ApiBand;
  anchor: string;
  direction: string;
  offset_seconds: number | null;
  offset_configurable: boolean;
  family: string;
  evid: string | null;
  label_short: string;
  label_long: string;
  sort_key: [number, number];
}

export interface ApiTimelineDeliveryMethod {
  id: number;
  method: number;
  method_label: string;
  enabled: boolean;
  sequence: number;
  rendering_type: number | null;
  theme_codename: string | null;
}

export interface ApiTimelineRow {
  id: number;
  name: string;
  event: string;
  enabled: boolean;
  origin: 'own' | 'inherited' | 'system';
  tenant: { id: number; code: string; name: string } | null;
  tour_override_mode: number;
  suppressed_by_child: boolean;
  booking_state: number;
  audience: {
    guests: boolean;
    observers: boolean;
    roles: { id: number; name: string }[];
  };
  tours: { id: number; name: string }[];
  language_policy: { id: number; codename: string } | null;
  block_ref_count: number | null;
  disable_default_blocks: boolean;
  delivery_method_ordering: number;
  timedelta_seconds: number | null;
  rule_summary: {
    match_type: number;
    count: number;
    rulesets: { id: number; codename: string | null; name: string }[];
  };
  delivery_methods: ApiTimelineDeliveryMethod[];
  position: ApiEventPosition;
  can_fire: boolean | null;
  cannot_fire_reason: string | null;
  canonical_event: string | null;
  warnings: string[];
  updated_ts: string | null;
}

export interface ApiTimeline {
  tenant: { id: number; code: string; name: string } | null;
  generated_ts: string;
  stats_available: boolean;
  notifications: ApiTimelineRow[];
}

export interface ApiTimelineStats {
  available: boolean;
  reason?: string;
  window_days: number;
  scope?: 'tenant' | 'platform';
  computed_ts?: string;
  by_notification: Record<
    string,
    { fires: number; errors: number; last_ts: string | null }
  >;
}

/** An entry from `/notification_events/?include_emitted=1`. */
export interface ApiEventCatalogEntry {
  code: string;
  family: string;
  entity_kind: string | null;
  label: string | null;
  source: 'catalog' | 'emitted' | 'both';
  position: ApiEventPosition;
  can_fire: boolean | null;
  cannot_fire_reason: string | null;
  canonical_code: string | null;
}

/** `system` selects the `tour_operator IS NULL` rows. */
export type TimelineTenantParam = number | 'system';

export function getTimeline(tenant: TimelineTenantParam): Promise<ApiTimeline> {
  return apiGet<ApiTimeline>(`/notification_timeline/?tour_operator_id=${tenant}`);
}

export function getTimelineStats(
  tenant: TimelineTenantParam,
  days = 30,
): Promise<ApiTimelineStats> {
  return apiGet<ApiTimelineStats>(
    `/notification_timeline/stats/?tour_operator_id=${tenant}&days=${days}`,
  );
}

/** The full event catalog including the metaclass-generated timed codes.
 *  Static per deploy — cache it hard (staleTime: Infinity). */
export function getEventCatalog(): Promise<ApiEventCatalogEntry[]> {
  return apiGet<{ events: ApiEventCatalogEntry[] }>(
    '/notification_events/?include_emitted=1',
  ).then((r) => r.events);
}

export interface ToggleEventResult {
  updated: number[];
  skipped: { id: number; reason: string }[];
  enabled: boolean;
}

export function toggleEvent(params: {
  tenant: TimelineTenantParam;
  event: string;
  enabled: boolean;
  ids?: number[];
}): Promise<ToggleEventResult> {
  return apiPost<ToggleEventResult>('/notification_timeline/toggle_event/', {
    tour_operator_id: String(params.tenant),
    event: params.event,
    enabled: params.enabled,
    ...(params.ids ? { ids: params.ids } : {}),
  });
}

export function toggleDeliveryMethod(params: {
  notificationId: number;
  dmId: number;
  enabled: boolean;
}): Promise<{ id: number; enabled: boolean; changed: boolean }> {
  return apiPost(`/notifications/${params.notificationId}/delivery_method_enabled/`, {
    dm_id: params.dmId,
    enabled: params.enabled,
  });
}

/** Single-notification enable/disable.
 *
 *  The existing detail PATCH, sending ONLY the `notification` section — the
 *  funnel treats `delivery_methods` / `block_refs` / `rules` as complete lists
 *  and reconciles by delete-and-recreate, so an overview screen must never
 *  include them. Kept here so the timeline has one import surface for all its
 *  mutations.
 */
export function setNotificationEnabled(
  id: number,
  enabled: boolean,
): Promise<unknown> {
  return apiPatch(`/notifications/${id}/`, { notification: { enabled } });
}
